import { mq } from "@/constants/breakpoints";
import {
  blueColor,
  darkColor,
  lightBlueColor,
  mutedBlueColor,
} from "@/constants/colors";
import { css } from "@emotion/react";
import Link from "next/link";
import { useMemo } from "react";
import HR from "../HR/HR";
import { H7Css, Heading } from "../Typography";
import { slugifyHeading } from "@/utils/richTextOptions";

interface TableOfContentsProps {
  blogPostJson: any;
}

const TableOfContents = ({ blogPostJson }: TableOfContentsProps) => {
  const groupedHeadings = useMemo(() => {
    const headings = blogPostJson.content.filter(
      (node: any) =>
        node.nodeType === "heading-2" || node.nodeType === "heading-3"
    );
    const groupedHeadings = [];
    let currentH2 = null;
    for (let i = 0; i < headings.length; i++) {
      const heading = headings[i];
      if (heading.nodeType === "heading-2") {
        currentH2 = {
          id: slugifyHeading("h2", heading.content[0].value),
          text: heading.content[0].value,
          children: [],
        };
        groupedHeadings.push(currentH2);
      } else if (heading.nodeType === "heading-3" && currentH2) {
        const child = {
          id: slugifyHeading("h3", heading.content[0].value),
          text: heading.content[0].value,
        };
        // @ts-ignore
        currentH2.children.push(child);
      }
    }
    return groupedHeadings;
  }, [blogPostJson]);

  return (
    <div
      css={css`
        margin-top: 30px;
        ${mq["sm"]} {
          margin-top: 40px;
        }
        ${mq["lg"]} {
          margin-top: 0;
        }
        ${mq["xl"]} {
          flex: 1;
        }
      `}
    >
      {groupedHeadings && (
        <div
          css={css`
            margin: auto;
            padding: 30px 24px;
            background-color: ${mutedBlueColor};
            border-radius: 12px;
            margin-bottom: 40px;
            ${mq["lg"]} {
              padding: 0;
              background-color: unset;
              margin-bottom: unset;
              max-width: 190px;
            }
          `}
        >
          <Heading
            css={css`
              color: ${darkColor};
              margin-bottom: 30px;
              ${H7Css}
            `}
          >
            Table of Contents
          </Heading>
          {groupedHeadings.map((el, index) => (
            <div key={index}>
              {index !== 0 && (
                <div
                  css={css`
                    margin: 16px 0;
                  `}
                >
                  <HR />
                </div>
              )}
              <Link
                key={el.id}
                href={`#${el.id}`}
                css={css`
                  display: block;
                `}
              >
                <Heading
                  css={css`
                    font-weight: 350;
                    font-size: 12px;
                    line-height: 14.4px;
                    color: ${darkColor};
                    transition: color 0.2s ease-in-out;
                    &:hover {
                      color: ${lightBlueColor};
                    }
                  `}
                >
                  {el.text}
                </Heading>
              </Link>
              {el.children.map((child: any) => (
                <div
                  key={child.id}
                  css={css`
                    align-items: center;
                    margin-top: 20px;
                    gap: 8px;
                    display: none;
                    ${mq["lg"]} {
                      display: flex;
                    }
                  `}
                >
                  <div
                    css={css`
                      width: 6px;
                      height: 6px;
                      background: ${blueColor};
                      border-radius: 50%;
                      margin-left: 10px;
                    `}
                  />
                  <Link
                    href={`#${child.id}`}
                    css={css`
                      display: block;
                    `}
                  >
                    <Heading
                      css={css`
                        font-weight: 350;
                        font-size: 12px;
                        line-height: 14.4px;
                        color: ${darkColor};
                        transition: color 0.2s ease-in-out;
                        &:hover {
                          color: ${lightBlueColor};
                        }
                      `}
                    >
                      {child.text}
                    </Heading>
                  </Link>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TableOfContents;
